/**
 * Discount applied only at export time (never persisted) — see
 * Componant/PdfExport/PdfExportDialog.js. `discount` is either null or
 * { type: 'percent' | 'fixed', value: number }.
 */
export function applyDiscount(basePrice, discount) {
    if (basePrice == null || !discount || !discount.value) return null;
    const amount = discount.type === 'percent'
        ? basePrice * (1 - discount.value / 100)
        : basePrice - discount.value;
    return Math.max(0, Math.round(amount * 100) / 100);
}

export function formatEUR(amount) {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
}

export function buildDiscountLabel(discount) {
    if (!discount || !discount.value) return null;
    return discount.type === 'percent' ? `-${discount.value} %` : `-${formatEUR(discount.value)}`;
}

export function buildExpirationLabel(expirationDate) {
    if (!expirationDate) return null;
    const formatted = new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric',
    }).format(expirationDate);
    return `Offre valable jusqu'au ${formatted}.`;
}

/**
 * Opens a blank popup window synchronously — call this immediately inside a
 * click handler, *before* any `await`, so the browser still attributes it to
 * the user gesture. If you open the window only after fetching data
 * (async), most browsers silently block it as an unsolicited popup.
 */
export function openBlankPrintWindow() {
    const printWindow = window.open('', '_blank', 'width=900,height=1000');
    if (printWindow) {
        printWindow.document.write(
            '<!doctype html><html><head><meta charset="utf-8"></head>' +
            '<body style="font-family:sans-serif;padding:40px;color:#666;">Génération du document…</body></html>'
        );
    }
    return printWindow;
}

/**
 * Fills an already-open window (see openBlankPrintWindow) with the final
 * document and triggers the browser's print dialog ("Enregistrer en PDF")
 * — chosen over a canvas/react-pdf export because the design relies on real
 * CSS (mask-image, clip-path, box-shadow, Google fonts) that only a real
 * browser rendering engine reproduces faithfully.
 */
export function writeAndPrint(printWindow, html) {
    if (!printWindow || printWindow.closed) {
        throw new Error("La fenêtre d'impression a été fermée avant la fin de la génération.");
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    let printed = false;
    const triggerPrint = () => {
        if (printed || printWindow.closed) return;
        printed = true;
        printWindow.focus();
        printWindow.print();
    };

    // Google Fonts load asynchronously after `load` fires too, so this is a
    // best-effort trigger, not a hard guarantee — a fallback timer covers
    // browsers where `load` doesn't fire for a document.write()'d page.
    printWindow.addEventListener('load', () => setTimeout(triggerPrint, 200));
    setTimeout(triggerPrint, 1200);
}

/**
 * Convenience wrapper for callers with no async work between the click and
 * having the final HTML ready (see ServicePackagesPage.js). If you need to
 * fetch data first, use openBlankPrintWindow() synchronously then
 * writeAndPrint() once ready instead (see ApplicationManagementPage.js).
 */
export function openPrintSheet(html) {
    const printWindow = openBlankPrintWindow();
    if (!printWindow) {
        throw new Error("Le navigateur a bloqué l'ouverture de la fenêtre d'impression (pop-up).");
    }
    writeAndPrint(printWindow, html);
}
